import { ButtonProps, Download } from '@sparemin/blockhead';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProBadge } from 'components/icons';
import useContextualUpgrade from 'hooks/useContextualUpgrade';
import { VideoZipRequestArgs } from 'redux/middleware/api/creation-service/types';
import { showError } from 'redux/modules/notification';
import { hasClipSuggestionsBulkExportEnabledSelector } from 'redux/modules/pricing';
import {
  createVideoZipRequest,
  currentVideoZipRequestDownloadInfoSelector,
  pollForVideoZipRequest,
} from 'redux/modules/video-zip-request';
import { Dispatch } from 'redux/types';
import { downloadLink } from 'utils/download';
import { useClipSelect } from '../ClipSelectContext';

export type UseDownloadAllButtonResult = ButtonProps & {
  content: React.ReactNode;
};

export default function useDownloadAllButton(): UseDownloadAllButtonResult {
  const {
    requestId,
    mediaType,
    clipsPageInfo,
    onUpdateSuggestions,
    isGeneratingPreviews,
    onDownloadAll,
    onExportAll,
  } = useClipSelect();

  const [isDownloading, setIsDownloading] = useState(false);
  const [prevIsExporting, setPrevIsExporting] = useState(false);
  const [hasGeneratedPreviews, setHasGeneratedPreviews] = useState(
    isGeneratingPreviews,
  );
  const hasPendingDownloadRef = useRef(false);

  const {
    isAllowedToDownload,
    zipFileDownloadUrl,
    hasVideoZipFile,
    isExportingZipFile,
  } = useSelector(currentVideoZipRequestDownloadInfoSelector);
  const hasClipSuggestionsBulkExportEnabled = useSelector(
    hasClipSuggestionsBulkExportEnabledSelector,
  );

  const { onUpgrade } = useContextualUpgrade();
  const dispatch = useDispatch<Dispatch>();

  const isLoading = isExportingZipFile || isDownloading;
  const isDownloadAvailable = isAllowedToDownload && !hasGeneratedPreviews;
  const shouldUseGhostVariant =
    !hasClipSuggestionsBulkExportEnabled ||
    !hasVideoZipFile ||
    isExportingZipFile ||
    !isAllowedToDownload ||
    hasGeneratedPreviews;

  const handleDownloadAll = useCallback(async () => {
    onDownloadAll();

    if (!hasPendingDownloadRef.current) {
      return;
    }

    try {
      setIsDownloading(true);

      await downloadLink(zipFileDownloadUrl, `${clipsPageInfo?.title}.zip`);
    } catch {
      dispatch(
        showError({
          message: 'Failed to download clips file. Please try again.',
          dismissAfterSec: 5,
        }),
      );
    } finally {
      setIsDownloading(false);
      hasPendingDownloadRef.current = false;
    }
  }, [clipsPageInfo?.title, dispatch, onDownloadAll, zipFileDownloadUrl]);

  const handleExportAll = useCallback(async () => {
    onExportAll();

    try {
      const requestArgs: VideoZipRequestArgs =
        mediaType === 'audio'
          ? { groupRequestId: requestId }
          : { creationRequestId: requestId };

      const { result } = await dispatch(createVideoZipRequest(requestArgs));
      await onUpdateSuggestions();

      setHasGeneratedPreviews(false);

      await dispatch(
        pollForVideoZipRequest({
          zipRequestId: result,
        }),
      );
    } catch {
      dispatch(
        showError({
          message: 'Failed to export clips. Please try again.',
          dismissAfterSec: 5,
        }),
      );
    }
  }, [dispatch, mediaType, onExportAll, onUpdateSuggestions, requestId]);

  const handlePress = useCallback(async () => {
    hasPendingDownloadRef.current = true;

    if (!hasClipSuggestionsBulkExportEnabled) {
      onUpgrade();
      return;
    }

    if (isDownloadAvailable) {
      handleDownloadAll();
      return;
    }

    if (!hasVideoZipFile || hasGeneratedPreviews) {
      handleExportAll();
    }
  }, [
    handleDownloadAll,
    handleExportAll,
    hasClipSuggestionsBulkExportEnabled,
    hasVideoZipFile,
    isDownloadAvailable,
    onUpgrade,
    hasGeneratedPreviews,
  ]);

  if (isExportingZipFile && !prevIsExporting) {
    setPrevIsExporting(isExportingZipFile);
  }

  if (isGeneratingPreviews && !hasGeneratedPreviews) {
    setHasGeneratedPreviews(isGeneratingPreviews);
  }

  useEffect(() => {
    if (prevIsExporting && !isExportingZipFile && isAllowedToDownload) {
      handleDownloadAll();
      setPrevIsExporting(false);
    }
  }, [
    isAllowedToDownload,
    handleDownloadAll,
    isExportingZipFile,
    prevIsExporting,
  ]);

  return {
    variant: shouldUseGhostVariant ? 'ghost' : 'solid',
    startIcon: isDownloadAvailable && <Download />,
    endIcon: !hasClipSuggestionsBulkExportEnabled && <ProBadge fill="white" />,
    isLoading,
    isDisabled: isGeneratingPreviews,
    content: isDownloadAvailable ? 'download all' : 'export all',
    onPress: handlePress,
  };
}
